// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AddCashModal, LastUpdatedInfo } from '@advisor-app/components';
import { Button, Skeleton } from '@mui/material';
import {
  Heading,
  OptionsSelector,
  SearchBar,
  SectionContainer,
} from '@shared/ui-kit/components';
import { useGetMutualFunds, useSearchMutualFunds } from '../hooks';
import { MutualFundsTable } from './mutual-funds-table';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useTranslation } from 'react-i18next';

export const MutualFunds = () => {
  const { t } = useTranslation();
  const {
    loadMutualFundOnRedux,
    transformedDataMF,
    handleCloseModal,
    openModalCash,
  } = useGetMutualFunds();

  const {
    filters,
    areFiltersLoaded,
    mutualFunds,
    isLoading,
    error,
    textSearch,
    handleTextSearch,
    pagination,
    resetFilters,
    applyFilters,
  } = useSearchMutualFunds();

  return (
    <div className="relative pt-12">
      <div className="absolute -top-9 right-0 flex items-center space-x-3">
        <Button variant="contained" color="secondary" onClick={applyFilters}>
          <Translation i18nKey="research.apply-filters" ns="research" />
        </Button>
        <Button variant="outlined" color="secondary" onClick={resetFilters}>
          <Translation i18nKey="research.reset-filters" ns="research" />
        </Button>
        <SearchBar
          value={textSearch}
          onChangeValue={handleTextSearch}
          placeholder={t('research.search-placeholder', { ns: 'research' })}
          dataTestId="mutual-fund-search-bar"
        />
      </div>

      <div className="mb-14 grid grid-cols-4 gap-6">
        {filters.map(item => (
          <div key={item.key}>
            <Heading variant="h5" className="pb-2" title={item.label} />
            {areFiltersLoaded ? (
              item.multiple ? (
                <OptionsSelector<string[]>
                  multiple
                  optionsAvailable={item.options}
                  onChangeValueSelected={item.onChange}
                  defaultText={item.placeholder}
                  selectType="gray"
                  optionSelected={item.selected}
                  dataTestId="mutual-fund-filter"
                  checkbox
                  searchBar={item.searchBar}
                  collapseSelectedLabelsAt={4}
                />
              ) : (
                <OptionsSelector
                  optionsAvailable={item.options}
                  onChangeValueSelected={item.onChange as (value: string) => void}
                  defaultText={item.placeholder}
                  selectType="gray"
                  optionSelected={item.selected as string}
                  dataTestId="mutual-fund-filter"
                  searchBar={item.searchBar}
                />
              )
            ) : (
              <Skeleton variant="rectangular" width="100%" height={35} />
            )}
          </div>
        ))}
      </div>
      {(isLoading || !areFiltersLoaded) && (
        <Skeleton variant="rectangular" width="100%" height={400} />
      )}

      {!isLoading && !error && areFiltersLoaded && (
        <SectionContainer className="!p-5">
          <LastUpdatedInfo data={mutualFunds} />

          <MutualFundsTable
            data={mutualFunds}
            pagination={pagination}
            onLoad={loadMutualFundOnRedux}
          />
        </SectionContainer>
      )}

      {error && areFiltersLoaded && (
        <SectionContainer className="mt-9 text-center text-base font-semibold text-primary-medium">
          <Translation i18nKey="research.mf-load-error" ns="research" />
        </SectionContainer>
      )}

      <AddCashModal
        dataPortfolio={transformedDataMF}
        handleClose={handleCloseModal}
        openModal={openModalCash}
        to="/analysis/stock"
      />
    </div>
  );
};
